import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { Router } from '@angular/router';
import { PublicError } from '@microsoft/microsoft-graph-types';
import { TranslateService } from '@ngx-translate/core';
import { IdexService } from 'app/idex.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Toast } from '../../shared/toast/toast';
import { OneDriveFoldersComponent } from '../one-drive-folders/one-drive-folders.component';
import { OneDriveItem } from '../onedrive-list/onedrive-item.type';
import { OneDriveItemService } from '../onedrive-page/onedrive-item.service';

export type OneDriveMoveDialogData = {
   /** OneDrive API access token */
   access_token: string;
   /** DriveItem to be moved */
   itemToMove?: OneDriveItem;
};

export type OneDriveMoveDialogResult = {
   /** destination folder ID */
   id: OneDriveItem['id'];
   /** destination folder name*/
   name: OneDriveItem['name'];
   /**
    * True if the item moving request is in progress. False otherwise.
    * This is for detecting when the user clicks 'cancel' immediately after clicking 'move' item.
    */
   isMovingInProgress: boolean;
   /** For checking the result of moving the item */
   movingResult$?: BehaviorSubject<'success' | 'fail'>;
};

@Component({
   selector: 'app-onedrive-move-dialog',
   templateUrl: './onedrive-move-dialog.component.html',
   styleUrls: ['./onedrive-move-dialog.component.css']
})
export class OneDriveMoveDialogComponent implements OnInit, AfterViewInit {
   @ViewChild('folders', { static: true }) OneDriveFoldersComponentRef: OneDriveFoldersComponent;

   /** DriveItem's ID of the root folder */
   protected rootFolderId: string;
   /** OneDrive API access token */
   protected access_token: string;
   /** Name of the DriveItem to be moved */
   protected sourceItemName: OneDriveItem['name'];
   /** ID of the parent DriveItem */
   protected parentFolderId: OneDriveItem['id'];
   /** DriveItem's ID and name of the selected destination folder to move the item to */
   private destinationFolderId: OneDriveItem['id'];
   /** DriveItem's name of the selected destination folder to move the item to */
   protected destinationFolderName: OneDriveItem['name'];

   /**
    * True if any folder is selected as a destination folder. False otherwise.
    *
    * This is used to disable the "Move" confirmation button if no destination folder is selected, and vice versa.
    */
   protected isDestinationSelected = false;

   /**
    * True if Root Folder is currently being selected as a destination folder. False otherwise.
    *
    * This is used to control (via ngClass) the text color of the Root Folder element when the Root is and isn't selected,
    */
   protected isRootBeingSelected = false;

   constructor(
      public dialogRef: MatDialogRef<OneDriveMoveDialogComponent, OneDriveMoveDialogResult>,
      @Inject(MAT_DIALOG_DATA) private data: OneDriveMoveDialogData,
      private service: IdexService,
      private translate: TranslateService,
      private router: Router,
      private itemService: OneDriveItemService,
      private toast: Toast
   ) {
      if (this.data) {
         this.access_token = this.data.access_token;
         this.service.onedriveGetItemFromId(this.access_token, '').subscribe(
            (rootItem) => {
               this.rootFolderId = rootItem.id;
            },
            (error) => {
               console.error(error);
               if (error.status === 401) {
                  this.handlezDOXTimeout()
               }
            }
         );
      }
   }

   ngOnInit() {
      this.sourceItemName = this.data?.itemToMove?.name;
      this.parentFolderId = this.data?.itemToMove?.parentReference?.id;
   }

   ngAfterViewInit() {
      this.OneDriveFoldersComponentRef.toggle();
   }

   onSelectRoot() {
      if (this.parentFolderId === this.rootFolderId) return;

      this.isDestinationSelected = true;
      this.translate.get('onedrive.rootFolder').subscribe((rootFolderName) => {
         this.destinationFolderName = rootFolderName;
      });
      this.destinationFolderId = this.rootFolderId;

      this.OneDriveFoldersComponentRef.unselect();
      this.isRootBeingSelected = true;
   }

   onSelectDestinationFolder(destinationFolder: OneDriveItem) {
      if (!destinationFolder) return;
      if (destinationFolder.id === this.parentFolderId) return;

      this.isDestinationSelected = true;
      this.destinationFolderName = destinationFolder.name;
      this.destinationFolderId = destinationFolder.id;

      this.isRootBeingSelected = false;
   }

   onCancel(): void {
      this.dialogRef.close({
         id: null,
         name: null,
         isMovingInProgress: this.isMovingInProgress,
         movingResult$: this.movingResult$
      });
   }

   protected isMovingInProgress = false;
   private movingResult$ = new BehaviorSubject<'success' | 'fail'>(null);
   onClickConfirmMove() {
      if (!this.data?.itemToMove) return;
      if (!this.destinationFolderId) return;
      if (this.isMovingInProgress) return;

      this.isMovingInProgress = true;
      this.movingResult$.next(null);
      this.itemService
         .update(this.data.access_token, this.data.itemToMove.id, { parentReference: { id: this.destinationFolderId } })
         .pipe(take(1))
         .subscribe(
            () => {
               this.isMovingInProgress = false;
               this.movingResult$.next('success');
               (this.translate.get('alert.move_to') as Observable<string>).subscribe((s) => {
                  this.toast.success(this.data.itemToMove.name + s + this.destinationFolderName, 5000);
               });
               this.dialogRef.close({
                  id: this.destinationFolderId,
                  name: this.destinationFolderName,
                  isMovingInProgress: this.isMovingInProgress,
                  movingResult$: this.movingResult$
               });
            },
            (error) => {
               this.isMovingInProgress = false;
               this.movingResult$.next('fail');
               this.handleMoveError(error, this.data.itemToMove, this.destinationFolderName);
            }
         );
   }

   private handleMoveError(error, itemToMove: OneDriveItem, destinaionFolderName: string) {
      console.error(error);

      const onedrive_error: PublicError = error?.error?.error;

      // Error from OneDrive API

      if (onedrive_error?.code === 'nameAlreadyExists') {
         this.translate.get('onedrive.error.nameAlreadyExists2').subscribe((s) => {
            this.toast.error(s, 7000);
         });
      }
      // OneDrive Auth Token Expired
      else if (onedrive_error?.code === 'InvalidAuthenticationToken') {
         this.handleOneDriveTokenExpired();
      }
      // OneDrive error: The item is locked, eTag mismatch, lock token does not match, etc.
      else if (error.status === 423) {
         this.translate.get('onedrive.error.423').subscribe((s) => {
            this.toast.error(s);
         });
      }
      // Other OneDrive error
      else if (onedrive_error) {
         this.translate
            .get('onedrive.error.cannotMove', {
               itemToMove: itemToMove.name,
               destination: destinaionFolderName,
               errorMessage: onedrive_error.message,
               errorCode: onedrive_error.innerError?.code ?? onedrive_error.code
            })
            .subscribe((s) => {
               this.toast.error(s, 7000);
            });
      }

      // Error from zDox: zDOX token is expired.
      else if (error.status === 401) {
         this.handlezDOXTimeout();
      } else {
         this.toast.error(error);
      }
   }

   /**
    * Handle Error 401: zDOX Timeout\
    * Redirect to login page and show a timeout message.
    */
   private handlezDOXTimeout() {
      this.translate.get('timeout').subscribe((s) => {
         this.toast.error(s);
      });
      this.dialogRef.close({
         id: null,
         name: null,
         isMovingInProgress: this.isMovingInProgress,
         movingResult$: this.movingResult$
      });
      this.router.navigate(['/login']);
   }

   /**
    * Handle OneDrive Token Expired
    *
    * Remove the expired token and reload page to let onedrive-auth-guard.service handle the login.
    */
   private handleOneDriveTokenExpired() {
      this.service.removeMicrosoftAuth();
      window.location.reload();
   }
}
